import { useEffect, useState } from "react";
import { cn } from "../../../utils/twMerge";

const SearchableDropdown = ({
  className,
  data,
  valueKey,
  displayKey,
  filterKey,
  setFilter,
  type,
  loading,
  defaultValue = "",
  editDefaultValue = () => {},
  required,
  label = "",
}) => {
  const [value, setValue] = useState("");
  const [uniqueDemats, setUniqueDemats] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleSelect = (value) => {
    if (!type) {
      setValue(value);

      if (value && value !== "") {
        setFilter((prev) => ({
          ...prev,
          [filterKey]: value,
        }));
      } else {
        setFilter((prev) => ({
          ...prev,
          [filterKey]: null,
        }));
      }
    }
  };

  // Handle search filtering
  useEffect(() => {
    if (searchText?.length > 0) {
      const filter = uniqueDemats?.filter((item) =>
        String(item[displayKey])
          ?.toLowerCase()
          ?.includes(searchText?.toLowerCase()),
      );
      setFilteredData(filter);
    } else {
      setFilteredData([]);
      setIsDropdownOpen(false);
    }
    if (defaultValue) {
      const filterDefault = uniqueDemats?.find(
        (item) => item[valueKey] === defaultValue,
      );
      setSearchText(filterDefault?.name);
    }
  }, [uniqueDemats, searchText, displayKey, defaultValue]);

  // Handle search input changes
  const handleOnChange = (e) => {
    editDefaultValue(null);

    const value = e.target.value;
    setSearchText(value);
    if (value?.length > 0) {
      setIsDropdownOpen(true);
    } else {
      setFilter();
    }
  };

  // Handle item selection
  const handleData = (value, name) => {
    setFilter(value);
    setIsDropdownOpen(false);
    setSearchText(name);
    setValue(value);
  };

  // Get unique demats for dropdown
  useEffect(() => {
    if (!data || data.length === 0) {
      setUniqueDemats([]);
      return;
    }

    const unique = {};
    const uniqueData = data.filter((item) => {
      const key = item[displayKey];
      if (key != null && !unique[key]) {
        unique[key] = true;
        return true;
      }
      return false;
    });

    setUniqueDemats(uniqueData);
  }, [data, displayKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex h-full w-full flex-col gap-2 rounded-[4px] bg-[#f5f5f5]">
      {type === "searchable" ? (
        <div className="dropdown-container relative">
          <div className="flex w-full flex-col rounded-[4px] bg-[#f5f5f5]">
            <div
              className={cn(
                "rounded bg-transparent px-2",
                required && "border-[1px] border-red-300",
              )}
            >
              <input
                value={searchText}
                placeholder="Search..."
                className="w-full bg-transparent py-[0.4rem] text-[14px] text-[#868686] focus:outline-none"
                onChange={handleOnChange}
              />
            </div>
          </div>

          {isDropdownOpen && (
            <div className={`scrollbar-custom absolute z-[500] mt-[2px] max-h-[300px] w-fit min-w-[220px] cursor-pointer overflow-y-auto rounded-md border-[1px] border-[#e3e3e3] bg-white p-1 shadow-lg ${className} `}>
              {loading ? (
                <div className="p-2 text-[#434343]">Loading data...</div>
              ) : filteredData?.length > 0 ? (
                filteredData.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    onClick={() => handleData(item[valueKey], item[displayKey])}
                    className={`w-full whitespace-nowrap cursor-pointer p-2 text-[#434343] hover:bg-[#99c8ff] ${
                      idx === filteredData.length - 1
                        ? ""
                        : "border-b-[1px] border-[#cfcfcf]"
                    }`}
                  >
                    {item[displayKey]}
                  </div>
                ))
              ) : (
                <div className="p-2 text-[#434343]">No data found</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="h-full shadow-sm rounded bg-transparent px-[1px] py-[1px]">
          <select
            className={`${
              className ? className : "w-full"
            } bg-transparent py-[0.4rem] text-[14px] text-[#868686] focus:outline-none`}
            value={value}
            onChange={(e) => handleSelect(e.target.value)}
          >
            <option value="" className="text-[#CFCFCF]">
              Select {label}
            </option>
            {uniqueDemats?.map((item, idx) => (
              <option
                key={item.id || idx}
                value={item[valueKey]}
                className="text-[#868686]"
              >
                {item[displayKey]}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
